import React, { useEffect, useState } from 'react';
import {
  Paper, Typography, Box, Button, Grid, CircularProgress, Alert,
  Select, InputLabel, MenuItem, FormControl, useTheme, useMediaQuery, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoIcon from '@mui/icons-material/Info';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../../api';

/**
 * DefiBadMatch - Page de saisie des matchs en défis
 * - Joueur 1 BAT Joueur 2
 * - Validation → reclassement live animé + persistance automatique
 * - Bouton "Annuler dernier match" pour undo
 * - Bouton Settings pour revenir aux réglages
 * - Bouton Règlement pour afficher les règles du tournoi
 * - Bouton retour vers accueil Badminton
 * - Persistance de la classe sélectionnée (localStorage)
 */

const STORAGE_KEY_CLASSE = 'badminton_selected_classe';

export default function DefiBadMatch({ initialClasse }) {
  const location = useLocation();
  const navigate = useNavigate();
  
  const [classes, setClasses] = useState([]);
  const [selectedClasse, setSelectedClasse] = useState('');
  const [ranking, setRanking] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [listFading, setListFading] = useState(false);
  const [playerA, setPlayerA] = useState('');
  const [playerB, setPlayerB] = useState('');
  const [errorMsg, setErrorMsg] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [reglementOpen, setReglementOpen] = useState(false);
  const defaultTopPoints = 40;

  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));
  const selectMinWidth = isMdUp ? 360 : (isSmUp ? 280 : '100%');

  // Initialiser avec priorité : state navigation > props > localStorage
  useEffect(() => {
    const fromNav = location?.state?.selectedClasse || location?.state?.initialClasse;
    const stored = localStorage.getItem(STORAGE_KEY_CLASSE);
    const initial = fromNav || initialClasse || stored || '';
    
    if (initial && initial !== selectedClasse) {
      setSelectedClasse(initial);
    }
  }, [initialClasse, location?.state?.selectedClasse, location?.state?.initialClasse, selectedClasse]);

  useEffect(() => {
    let mounted = true;
    api.get('/classes')
      .then(res => { if (!mounted) return; setClasses(Array.isArray(res.data) ? res.data : []); })
      .catch(err => { console.warn('Erreur chargement classes', err); setClasses([]); });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!selectedClasse) { 
      setRanking([]);
      localStorage.removeItem(STORAGE_KEY_CLASSE);
      return;
    }
    
    // Sauvegarder dans localStorage
    localStorage.setItem(STORAGE_KEY_CLASSE, selectedClasse);
    
    refreshEleves({ sortByPoints: true, animate: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClasse]);

  const refreshEleves = async (options = { sortByPoints: true, animate: false }) => {
    if (!selectedClasse) return [];
    setLoading(true);
    setErrorMsg(null);
    
    try {
      if (options.animate) {
        setListFading(true);
        await new Promise(r => setTimeout(r, 200));
      }

      // 🔧 APPEL À LA ROUTE SPÉCIFIQUE BADMINTON
      const ref = await api.get(`/badminton/eleves?classe=${encodeURIComponent(selectedClasse)}`);
      const list = Array.isArray(ref.data) ? ref.data : [];

      const computed = list.map((e, i) => {
        const pts = (typeof e.points === 'number') ? e.points : Math.max(0, defaultTopPoints - i);
        return { ...e, points: pts };
      });

      const final = options.sortByPoints
        ? computed.slice().sort((a, b) => ((b.points || 0) - (a.points || 0)))
        : computed;

      setRanking(final);
      
      if (options.animate) {
        await new Promise(r => setTimeout(r, 60));
        setListFading(false);
      }
      
      return final;
    } catch (err) {
      console.warn('refreshEleves error', err);
      setErrorMsg('Erreur chargement élèves — voir console');
      setRanking([]);
      setListFading(false);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const persistOrderedRanking = async (orderedArray) => {
    if (!selectedClasse) return;
    try {
      const orderedIds = orderedArray.map(e => e._id);
      await api.post(`/badminton/classe/${selectedClasse}/save-order`, { orderedIds });
      console.info('Ordre trié persisté automatiquement.');
    } catch (err) {
      console.warn('Erreur persistance ordre', err);
    }
  };

  const computeDelta = (winnerPos, loserPos) => {
    if (winnerPos === loserPos) return 0;
    if (winnerPos > loserPos) {
      const gap = winnerPos - loserPos;
      if (gap === 1) return 3;
      if (gap === 2) return 4;
      return 5;
    } else {
      const gap = loserPos - winnerPos;
      if (gap === 1) return 3;
      if (gap === 2) return 2;
      return 1;
    }
  };

  const handleSaveMatch = async () => {
    setErrorMsg(null); setSuccessMsg(null);
    if (!selectedClasse) return setErrorMsg('Sélectionne d\'abord une classe.');
    if (!playerA || !playerB) return setErrorMsg('Sélectionne les deux joueurs.');
    if (playerA === playerB) return setErrorMsg('Les deux joueurs doivent être différents.');

    const winnerId = playerA;
    const loserId = playerB;
    const posWinner = ranking.findIndex(r => String(r._id) === String(winnerId));
    const posLoser = ranking.findIndex(r => String(r._id) === String(loserId));
    if (posWinner === -1 || posLoser === -1) return setErrorMsg('Joueurs non trouvés dans le classement.');

    const delta = computeDelta(posWinner, posLoser);
    if (delta === 0) return setErrorMsg('Delta invalide.');

    setSaving(true);
    try {
      const payload = {
        classeId: selectedClasse,
        updates: [
          { eleveId: winnerId, delta: delta },
          { eleveId: loserId, delta: -Math.abs(delta) }
        ],
        match: {
          type: 'DEFI',
          winner: winnerId,
          loser: loserId,
          delta
        }
      };

      const res = await api.post('/badminton/defi/result', payload);
      if (res.status >= 200 && res.status < 300) {
        const final = await refreshEleves({ sortByPoints: true, animate: true });
        if (final && final.length) {
          await persistOrderedRanking(final);
        }
        setSuccessMsg('Match validé ! Classement mis à jour et ordre persisté.');
        setPlayerA(''); setPlayerB('');
      } else {
        setErrorMsg('Réponse inattendue du serveur.');
      }
    } catch (err) {
      console.error('handleSaveMatch error', err);
      setErrorMsg(err?.response?.data?.error || err?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleUndoLastMatch = async () => {
    setErrorMsg(null);
    setSuccessMsg(null);
    if (!selectedClasse) return setErrorMsg('Sélectionne d\'abord une classe.');
    if (!window.confirm('Annuler le dernier match pour cette classe ? Cette action inversera le dernier calcul de points.')) return;

    setSaving(true);
    try {
      const res = await api.post('/badminton/defi/undo-last', { classeId: selectedClasse });
      if (res.status >= 200 && res.status < 300) {
        const final = await refreshEleves({ sortByPoints: true, animate: true });
        if (final && final.length) {
          await persistOrderedRanking(final);
        }
        setSuccessMsg('Dernier match annulé et classement restauré.');
      } else {
        setErrorMsg('Impossible d\'annuler le dernier match (réponse inattendue).');
      }
    } catch (err) {
      console.error('handleUndoLastMatch error', err);
      setErrorMsg(err?.response?.data?.error || err?.message || 'Erreur lors de l\'annulation');
    } finally {
      setSaving(false);
    }
  };

  const goToSettings = () => {
    navigate('/apps/badminton/defi/manage', { state: { selectedClasse } });
  };

  const goToBadmintonHome = () => {
    navigate('/apps/badminton');
  };

  return (
    <Paper sx={{ p: 2, mt: 3 }}>
      <Grid container alignItems="center" spacing={1}>
        <Grid item xs={2} sx={{ textAlign: 'left' }}>
          <IconButton 
            aria-label="Retour accueil Badminton" 
            onClick={goToBadmintonHome} 
            size="large" 
            sx={{ color: '#6b6b6b' }}
            title="Retour à l'accueil Badminton"
          >
            <ArrowBackIcon />
          </IconButton>
        </Grid>
        <Grid item xs={8} sx={{ textAlign: 'center' }}>
          <Typography variant="h6">MATCHS EN DÉFIS</Typography>
        </Grid>
        <Grid item xs={2} sx={{ textAlign: 'right' }}>
          <IconButton 
            aria-label="Réglages du tournoi" 
            onClick={goToSettings} 
            size="large" 
            sx={{ color: '#6b6b6b' }}
            title="Réglages du tournoi en défis"
          >
            <SettingsIcon />
          </IconButton>
        </Grid>
      </Grid>

      <Box sx={{ mt: 2 }}>
        <FormControl fullWidth sx={{ minWidth: selectMinWidth, mb: 2 }}>
          <InputLabel id="classe-select-match">Classe</InputLabel>
          <Select
            labelId="classe-select-match"
            value={selectedClasse}
            label="Classe"
            onChange={(e) => setSelectedClasse(e.target.value)}
          >
            <MenuItem value="">Sélectionner</MenuItem>
            {classes.map(c => <MenuItem key={c._id} value={c._id}>{c.nom || c.name}</MenuItem>)}
          </Select>
        </FormControl>

        {loading ? (
          <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>
        ) : selectedClasse ? (
          <>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={5}>
                <FormControl fullWidth sx={{ minWidth: selectMinWidth }}>
                  <InputLabel id="sel-playerA">Joueur 1 (gagne)</InputLabel>
                  <Select
                    labelId="sel-playerA"
                    value={playerA}
                    label="Joueur 1 (gagne)"
                    onChange={e => setPlayerA(e.target.value)}
                  >
                    <MenuItem value="">Sélectionner</MenuItem>
                    {ranking.map(e => <MenuItem key={e._id} value={e._id}>{e.prenom} {e.nom} — {e.points} pts</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography sx={{ fontWeight: 'bold', fontSize: '1.2rem' }}>BAT</Typography>
              </Grid>

              <Grid item xs={12} md={5}>
                <FormControl fullWidth sx={{ minWidth: selectMinWidth }}>
                  <InputLabel id="sel-playerB">Joueur 2 (perd)</InputLabel>
                  <Select
                    labelId="sel-playerB"
                    value={playerB}
                    label="Joueur 2 (perd)"
                    onChange={e => setPlayerB(e.target.value)}
                  >
                    <MenuItem value="">Sélectionner</MenuItem>
                    {ranking.map(e => <MenuItem key={e._id} value={e._id}>{e.prenom} {e.nom} — {e.points} pts</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <Button
                  variant="contained"
                  onClick={handleSaveMatch}
                  disabled={saving}
                  sx={{
                    bgcolor: '#C8E6C9',
                    color: '#0b3d14',
                    '&:hover': { bgcolor: '#B2DFBB' }
                  }}
                >
                  {saving ? 'Validation...' : '✅ Valider le match'}
                </Button>

                <Button
                  variant="outlined"
                  onClick={handleUndoLastMatch}
                  disabled={saving}
                  sx={{
                    borderColor: '#FFDAB9',
                    color: '#6b3d00',
                    '&:hover': { backgroundColor: '#FFF2E6' }
                  }}
                >
                  ↩️ Annuler dernier match
                </Button>

                <Button
                  variant="text"
                  onClick={() => { setPlayerA(''); setPlayerB(''); setErrorMsg(null); setSuccessMsg(null); }}
                  sx={{ color: '#7b6cff' }}
                >
                  🔄 Réinitialiser la saisie
                </Button>

                <Button
                  variant="contained"
                  startIcon={<InfoIcon />}
                  onClick={() => setReglementOpen(true)}
                  sx={{
                    bgcolor: '#FFCDD2',
                    color: '#C62828',
                    fontWeight: 'bold',
                    '&:hover': { 
                      bgcolor: '#EF9A9A',
                      boxShadow: '0 4px 8px rgba(198, 40, 40, 0.2)'
                    }
                  }}
                >
                  Règlement du tournoi
                </Button>
              </Grid>
            </Grid>

            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>📊 Classement en direct</Typography>
              <Box sx={{
                mt: 1,
                transition: 'opacity 300ms ease, transform 350ms ease',
                opacity: listFading ? 0 : 1,
                transform: listFading ? 'translateY(-8px)' : 'translateY(0)'
              }}>
                {ranking.map((r, i) => (
                  <Box key={r._id} sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    p: 1, 
                    borderBottom: '1px solid #eee',
                    bgcolor: i === 0 ? '#fff9c4' : i === 1 ? '#fff3e0' : i === 2 ? '#ffe0b2' : 'inherit'
                  }}>
                    <Typography>
                      {i === 0 && '🥇 '}
                      {i === 1 && '🥈 '}
                      {i === 2 && '🥉 '}
                      {i + 1}. {r.prenom} {r.nom}
                    </Typography>
                    <Typography sx={{ fontWeight: 'bold' }}>{r.points} pts</Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            {errorMsg && <Box sx={{ mt: 2 }}><Alert severity="error">{errorMsg}</Alert></Box>}
            {successMsg && <Box sx={{ mt: 2 }}><Alert severity="success">{successMsg}</Alert></Box>}
          </>
        ) : null}
      </Box>

      {/* Dialog Règlement du tournoi */}
      <Dialog 
        open={reglementOpen} 
        onClose={() => setReglementOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#E3F2FD', color: '#1565C0', fontWeight: 'bold' }}>
          🏸 Règlement du Tournoi en Défis
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="h6" sx={{ mb: 2, color: '#1976D2' }}>📋 Règles générales</Typography>
          <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
            <li>
              <Typography>
                <strong>Chaque joueur doit défier 2 fois minimum</strong> un adversaire et <strong>être défié 2 fois minimum</strong>.
              </Typography>
            </li>
            <li>
              <Typography>
                Le nombre de défis doit être <strong>le même pour tous les élèves</strong>.
              </Typography>
            </li>
            <li>
              <Typography>
                Je <strong>n'ai pas le droit de refuser un défi</strong> (sauf si j'ai déjà été défié assez de fois).
              </Typography>
            </li>
            <li>
              <Typography>
                Je <strong>n'ai pas le droit de défier 2 fois la même personne</strong>.
              </Typography>
            </li>
          </Box>

          <Typography variant="h6" sx={{ mt: 3, mb: 2, color: '#1976D2' }}>⚔️ Système de points</Typography>
          
          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 'bold', color: '#2E7D32' }}>
            🏆 Si je <u>GAGNE</u> mon match contre une personne :
          </Typography>
          <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
            <li>
              <Typography>
                <strong>Du même classement que moi :</strong> je gagne <span style={{ color: '#2E7D32', fontWeight: 'bold' }}>+3 points</span> et il perd <span style={{ color: '#C62828', fontWeight: 'bold' }}>-3 points</span>
              </Typography>
            </li>
            <li>
              <Typography>
                <strong>D'1 classement au-dessus de moi :</strong> je gagne <span style={{ color: '#2E7D32', fontWeight: 'bold' }}>+4 points</span> et il perd <span style={{ color: '#C62828', fontWeight: 'bold' }}>-4 points</span>
              </Typography>
            </li>
            <li>
              <Typography>
                <strong>De 2 classements ou plus au-dessus de moi :</strong> je gagne <span style={{ color: '#2E7D32', fontWeight: 'bold' }}>+5 points</span> et il perd <span style={{ color: '#C62828', fontWeight: 'bold' }}>-5 points</span>
              </Typography>
            </li>
          </Box>

          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 'bold', color: '#C62828' }}>
            ❌ Si je <u>PERDS</u> mon match contre une personne :
          </Typography>
          <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
            <li>
              <Typography>
                <strong>Du même classement que moi :</strong> je perds <span style={{ color: '#C62828', fontWeight: 'bold' }}>-3 points</span> et il gagne <span style={{ color: '#2E7D32', fontWeight: 'bold' }}>+3 points</span>
              </Typography>
            </li>
            <li>
              <Typography>
                <strong>D'1 classement au-dessus de moi :</strong> je perds <span style={{ color: '#C62828', fontWeight: 'bold' }}>-2 points</span> et il gagne <span style={{ color: '#2E7D32', fontWeight: 'bold' }}>+2 points</span>
              </Typography>
            </li>
            <li>
              <Typography>
                <strong>De 2 classements ou plus au-dessus de moi :</strong> je perds <span style={{ color: '#C62828', fontWeight: 'bold' }}>-1 point</span> et il gagne <span style={{ color: '#2E7D32', fontWeight: 'bold' }}>+1 point</span>
              </Typography>
            </li>
          </Box>

          <Box sx={{ mt: 3, p: 2, bgcolor: '#FFF9C4', borderRadius: 1, border: '1px solid #FBC02D' }}>
            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
              💡 <strong>Astuce :</strong> Plus je défie des personnes au-dessus de moi dans le classement et que je gagne, plus je gagne de points !
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReglementOpen(false)} variant="contained" color="primary">
            Compris !
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}